import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const DesignPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent mb-6">
            Creative Design Services
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Transform your brand with stunning visual design that captures attention and drives results for your business.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-palette-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Professional Brand Image</h3>
              <p className="text-gray-600">Create a memorable brand identity that builds trust and credibility with your customers.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-eye-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Stand Out from Competition</h3>
              <p className="text-gray-600">Unique designs that differentiate your business and attract more customers.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-trending-up-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Increase Conversions</h3>
              <p className="text-gray-600">Strategic design elements that guide customers toward taking action.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Our Design Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-pink-100 to-purple-100 p-6 rounded-xl">
              <i className="ri-layout-line text-3xl text-pink-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Logo Design</h4>
              <p className="text-sm text-gray-600">Custom logos that represent your brand perfectly</p>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-blue-100 p-6 rounded-xl">
              <i className="ri-image-line text-3xl text-purple-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Brand Identity</h4>
              <p className="text-sm text-gray-600">Complete visual identity systems and guidelines</p>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-teal-100 p-6 rounded-xl">
              <i className="ri-file-text-line text-3xl text-blue-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Marketing Materials</h4>
              <p className="text-sm text-gray-600">Brochures, flyers, and promotional designs</p>
            </div>
            <div className="bg-gradient-to-br from-teal-100 to-green-100 p-6 rounded-xl">
              <i className="ri-smartphone-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Digital Graphics</h4>
              <p className="text-sm text-gray-600">Social media graphics and web banners</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-yellow-100 p-6 rounded-xl">
              <i className="ri-printer-line text-3xl text-green-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Print Design</h4>
              <p className="text-sm text-gray-600">Business cards, letterheads, and signage</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-presentation-line text-3xl text-yellow-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Presentation Design</h4>
              <p className="text-sm text-gray-600">Professional slides and pitch decks</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Follow Our Creative Journey</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-teal-500 to-green-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Brand?</h2>
          <p className="text-xl mb-8">Let's create stunning designs that make your business shine!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-purple-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-purple-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DesignPage;